import { currentPortalHost } from "./portalHost";

/**
 * Affiliate Google sign-in, as a full-page redirect through the backend.
 *
 * Google's in-page popup flow only runs on origins registered by hand in the
 * Cloud Console, with no wildcards, so it failed with `origin_mismatch` on
 * every white-label portal (`<slug>.reveshare.com`). The backend flow gives
 * Google one fixed callback URL instead; the browser leaves this page, comes
 * back on `/affiliate/auth/google/complete?ticket=…`, and that page redeems
 * the ticket for the sign-in result (see pages/affiliate/auth/GoogleComplete).
 *
 * Because the page unloads, anything we need afterwards is parked in
 * sessionStorage: it survives the round trip in the same tab and nowhere else.
 */

const RESUME_KEY = "googleSignIn.resume";

export interface GoogleSignInResume {
  /** Path + query of the page that started the sign-in, to return to on error or onboarding. */
  from: string;
  /** Safe same-origin destination once signed in, if the page had one. */
  returnUrl?: string;
}

export function beginGoogleSignIn(resume: GoogleSignInResume): void {
  try {
    sessionStorage.setItem(RESUME_KEY, JSON.stringify(resume));
  } catch {
    // Storage unavailable: the completion page falls back to its defaults.
  }
  const params = new URLSearchParams({
    origin: window.location.origin,
    portalHost: currentPortalHost(),
  });
  window.location.assign(
    `${import.meta.env.VITE_API_URL}/affiliates/auth/google/start?${params.toString()}`
  );
}

/** Reads and clears what `beginGoogleSignIn` parked. */
export function takeGoogleSignInResume(): GoogleSignInResume | null {
  try {
    const raw = sessionStorage.getItem(RESUME_KEY);
    sessionStorage.removeItem(RESUME_KEY);
    return raw ? (JSON.parse(raw) as GoogleSignInResume) : null;
  } catch {
    return null;
  }
}

/** Shape the login/signup pages receive in `location.state` after a failure. */
export interface GoogleSignInErrorState {
  googleError?: string;
}
